import React, {useMemo} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton/index.ts'
import IconX from '@static/images/icon_x.svg'
import {type TradeResponse, TradeActions, type TradeValidator} from '@colonist/game-mechanic-validators'
import {TradeResponseStatus} from '@colonist/game-states'
import {type TranslationKeys} from '@colonist/i18n'
import styles from '../UIGameTradeOffers.module.scss'
import clsx from 'clsx'
import {T} from '@colonist/ui'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext.tsx'
import {GameTypeEnum, ModeSettingEnum} from '@colonist/game-base-types'
import {useGameStore} from '../../../store/GameStoreContext.ts'

export interface UIGameTradeOfferRejectTradeButtonProps {
    className?: string | undefined
    tradeValidator: TradeValidator
    isOtherButtonsClicked: boolean
    setIsOtherButtonsClicked: (isOtherButtonsClicked: boolean) => void
    isGameInBackground: boolean
    disableTooltips?: boolean | undefined
}

export const UIGameTradeOfferRejectTradeButton = ({
    className,
    tradeValidator,
    isOtherButtonsClicked,
    setIsOtherButtonsClicked,
    isGameInBackground,
    disableTooltips,
}: UIGameTradeOfferRejectTradeButtonProps) => {
    const isPaused = useGameStore((state) => state.gamePause.isPaused)
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const myColor = useGameStore((state) => state.myColor)
    const myResponseStatus = tradeValidator.getPlayerResponseState(myColor)
    const highlighted = myResponseStatus?.responseStatus == TradeResponseStatus.Rejected
    const {sendForceOpenGuideBubble} = useUIGameSocketContext()
    const gameSettings = useGameStore((state) => state.gameSettings)
    const sendUpdateTradeResponse = useGameStore((state) => state.gameTradeOffers.sendUpdateTradeResponse)

    const rejectAction = tradeValidator.getRejectTradeActionStatus(myColor)
    const isHidden = !rejectAction.displayActionButton
    const tooltipText: TranslationKeys = 'strings:game.tooltips.rejectTrade'
    const helperText = rejectAction.errorMessageIfCantTakeAction
    const isEnabled = rejectAction.canTakeAction && !isMyColorSpectator
    const isEmbargoed = myResponseStatus?.responseStatus == TradeResponseStatus.Embargoed

    const isAutoTradeRejectionDisabled = useMemo(() => {
        const isBeginnerMode = gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        const isTournamentRoom = gameSettings.gameType == GameTypeEnum.CreatedTournamentRoom
        return isBeginnerMode || isTournamentRoom
    }, [gameSettings.modeSetting, gameSettings.gameType])

    const timeToAutoRejectWhenGameInBackground = useMemo(() => {
        if(!isGameInBackground) return 0
        const isMyResponsePending = myResponseStatus?.responseStatus == TradeResponseStatus.PendingResponse
        const amICreator = tradeValidator.state.creator == myColor
        if(!isMyResponsePending || amICreator) return 0
        return 1000
    }, [isGameInBackground, myResponseStatus, tradeValidator.state.creator, myColor])

    const timeToAutoRejectMs = useMemo(() => {
        const timeToAutoRejectDefault = tradeValidator.getTradeAutoRejectionTime(gameSettings.gameSpeed, myColor)
        return timeToAutoRejectWhenGameInBackground || timeToAutoRejectDefault
    }, [gameSettings.gameSpeed, myColor, timeToAutoRejectWhenGameInBackground])

    const shouldAutoReject = tradeValidator.state.creator !== myColor
        && !isOtherButtonsClicked
        && !highlighted
        && !isAutoTradeRejectionDisabled
        && !isMyColorSpectator
        && !isPaused

    const handleClick = () => {
        const isAcceptForcedBeginnerMode = tradeValidator.state.forcedResponse == TradeResponseStatus.Accepted
        if(isAcceptForcedBeginnerMode) {
            sendForceOpenGuideBubble()
            return
        }
        const rejectTrade: TradeResponse = {
            id: tradeValidator.state.id,
            response: TradeActions.Reject,
        }
        setIsOtherButtonsClicked(true)
        sendUpdateTradeResponse(rejectTrade)
    }

    if(isHidden) return null
    return (
        <UIGameActionButton
            tooltipContent={<T id={tooltipText}/>}
            tooltipDisabled={disableTooltips}
            className={clsx(styles.tradeButton, className)}
            iconPath={IconX}
            onClick={handleClick}
            buttonType="trade"
            tooltipPosition='bottom'
            centerTextTooltip
            helperText={helperText}
            disabled={!isEnabled}
            isEmbargoed={isEmbargoed}
            highlight={highlighted}
            showCooldownMask={shouldAutoReject}
            cooldownTimeMs={timeToAutoRejectMs}
        />
    )
}
